import React, { useState, useEffect } from 'react';
import { fetchAPI } from '../../utils/api';
import { FaTruck, FaSearch, FaDownload, FaCalendarAlt, FaCheck, FaPaperPlane, FaBox } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

export default function AdminDelivery() {
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [filterStatus, setFilterStatus] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });

    // Modal
    const [trackingModal, setTrackingModal] = useState({ show: false, id: null });
    const [trackingNo, setTrackingNo] = useState('');

    useEffect(() => {
        loadDeliveries();
    }, [filterStatus, dateRange]); // Reload when strict filters change

    // Debounce search could be added here, but for now we'll search on enter or button click if needed, 
    // or just let the effect run if we add searchTerm to dependency. 
    // Current requirement: "Search for index number". Let's do it live or on button? Live is better.
    useEffect(() => {
        const timeout = setTimeout(() => {
            loadDeliveries();
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    const loadDeliveries = async () => {
        setLoading(true);
        try {
            // Build query params
            const params = new URLSearchParams();
            if (filterStatus !== 'All') params.append('status', filterStatus);
            if (searchTerm) params.append('search', searchTerm);
            if (dateRange.start) params.append('start_date', dateRange.start);
            if (dateRange.end) params.append('end_date', dateRange.end);

            const data = await fetchAPI(`/admin/get_deliveries.php?${params.toString()}`);
            setDeliveries(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load deliveries");
        } finally {
            setLoading(false);
        }
    };

    const handleMarkSent = async () => {
        if (!trackingNo) return toast.error("Please enter a tracking number");
        try {
            await fetchAPI('/admin/update_delivery.php', {
                method: 'POST',
                body: JSON.stringify({
                    id: trackingModal.id,
                    status: 'Sent',
                    tracking_no: trackingNo
                })
            });
            toast.success("Delivery marked as Sent!");
            setTrackingModal({ show: false, id: null });
            setTrackingNo('');
            loadDeliveries();
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    // CSV Download Logic
    const handleDownload = () => {
        if (deliveries.length === 0) return toast.error("No data to download");

        const headers = ["ID", "Student Name", "Index Number", "Phone", "Address", "Month", "Status", "Tracking No", "Sent Date", "Created At"];
        const csvRows = [headers.join(',')];

        deliveries.forEach(d => {
            const row = [
                d.id,
                `"${d.student_name}"`, // Quote strings with commas
                d.index_number,
                d.phone,
                `"${d.address ? d.address.replace(/\n/g, ' ') : ''}"`,
                d.month,
                d.status,
                d.tracking_no || '',
                d.sent_date || '',
                d.created_at
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `deliveries_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            {/* Header Area */}
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2 flex items-center gap-2">
                        <FaTruck className="text-blue-600" />
                        Tute Delivery Management
                    </h1>
                    <p className="text-gray-500 font-medium">Manage student study pack deliveries and tracking.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleDownload}
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-emerald-200"
                    >
                        <FaDownload /> Download CSV
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Total Deliveries</p>
                        <p className="text-3xl font-bold text-gray-800 mt-1">{deliveries.length}</p>
                    </div>
                    <div className="bg-blue-50 text-blue-600 p-3 rounded-xl">
                        <FaBox size={24} />
                    </div>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Pending</p>
                        <p className="text-3xl font-bold text-amber-600 mt-1">{deliveries.filter(d => d.status === 'Pending').length}</p>
                    </div>
                    <div className="bg-amber-50 text-amber-600 p-3 rounded-xl">
                        <FaTruck size={24} />
                    </div>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Sent</p>
                        <p className="text-3xl font-bold text-emerald-600 mt-1">{deliveries.filter(d => d.status === 'Sent').length}</p>
                    </div>
                    <div className="bg-emerald-50 text-emerald-600 p-3 rounded-xl">
                        <FaCheck size={24} />
                    </div>
                </div>
            </div>

            {/* Controls Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col xl:flex-row gap-4">

                {/* Search */}
                <div className="flex-1 relative">
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search Index Number, Name or Month..."
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Filters Group */}
                <div className="flex flex-wrap items-center gap-3">

                    {/* Status Filter */}
                    <div className="flex p-1 bg-gray-50 rounded-xl border border-gray-200">
                        {['All', 'Pending', 'Sent'].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilterStatus(f)}
                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${filterStatus === f
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>

                    {/* Date Picker */}
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-2 rounded-xl">
                        <FaCalendarAlt className="text-gray-400" />
                        <input
                            type="date"
                            className="bg-transparent text-xs font-bold text-gray-600 focus:outline-none"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        />
                        <span className="text-gray-300">to</span>
                        <input
                            type="date"
                            className="bg-transparent text-xs font-bold text-gray-600 focus:outline-none"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        />
                    </div>
                </div>
            </div>

            {/* Results Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500 font-bold">
                                <th className="p-5">Student Info</th>
                                <th className="p-5">Delivery Details</th>
                                <th className="p-5">Status</th>
                                <th className="p-5 text-right bg-gray-50">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="p-10 text-center text-gray-400 animate-pulse">
                                        Loading data...
                                    </td>
                                </tr>
                            ) : deliveries.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="p-10 text-center">
                                        <div className="flex flex-col items-center justify-center text-gray-300">
                                            <FaBox size={40} className="mb-3 opacity-20" />
                                            <p className="text-sm font-medium">No deliveries found matching filters.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                deliveries.map(d => (
                                    <tr key={d.id} className="group hover:bg-blue-50/30 transition-colors">
                                        <td className="p-5 align-top">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-gray-900">{d.student_name}</span>
                                                <span className="text-xs text-blue-600 font-mono mt-1 bg-blue-50 px-2 py-0.5 rounded-md w-fit">{d.index_number}</span>
                                                <span className="text-xs text-gray-400 mt-1">{d.phone}</span>
                                            </div>
                                        </td>
                                        <td className="p-5 align-top">
                                            <div className="max-w-xs">
                                                <p className="text-xs text-gray-600 leading-relaxed mb-2 line-clamp-2" title={d.address}>
                                                    {d.address || <span className="text-red-300 italic">No Address Found</span>}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Month</span>
                                                    <span className="text-xs font-bold text-gray-800">{d.month}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-5 align-top">
                                            <div className="flex flex-col items-start gap-1">
                                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${d.status === 'Sent' ? 'bg-emerald-100 text-emerald-700' :
                                                    d.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {d.status}
                                                </span>
                                                {d.tracking_no && (
                                                    <span className="text-[10px] font-mono text-gray-500 flex items-center gap-1">
                                                        <FaPaperPlane size={8} /> {d.tracking_no}
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-gray-300 mt-1">
                                                    {new Date(d.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-5 text-right bg-gray-50/30 align-top group-hover:bg-white/0 transition-colors">
                                            {d.status === 'Pending' ? (
                                                <button
                                                    onClick={() => setTrackingModal({ show: true, id: d.id })}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 shadow-sm shadow-blue-200 transition-all hover:-translate-y-0.5"
                                                >
                                                    Mark Sent
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => setTrackingModal({ show: true, id: d.id })}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-500 rounded-lg text-xs font-bold hover:bg-gray-50 hover:text-blue-600 transition-all"
                                                >
                                                    Edit Track
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Tracking Modal */}
            {trackingModal.show && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl transform transition-all animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-1">Update Tracking</h3>
                            <p className="text-sm text-gray-500 mb-6">Enter the tracking number for this package.</p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Tracking Number</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                                        placeholder="e.g. SLP882910"
                                        value={trackingNo}
                                        onChange={(e) => setTrackingNo(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setTrackingModal({ show: false, id: null })}
                                        className="flex-1 px-4 py-2.5 bg-gray-50 text-gray-600 rounded-xl text-sm font-bold hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleMarkSent}
                                        className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all hover:-translate-y-0.5"
                                    >
                                        Update Status
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
